import React, { useMemo, useState, useEffect, createContext, useContext } from 'react';
import { Layout, ConfigProvider, Progress, Spin, Grid } from 'antd';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import Router from '@/router';
import Navigation from './Navigation';
import { selectAuth } from '@/redux/auth/selectors';
import HeaderContent from '@/app/HeaderContent';
import AppFooter from '@/pages/Footer';
import useIdleTimer from '@/redux/auth/useIdleTimer';
import { request } from '@/request';
import cryptoHelper from '@/utils/crypto';
import { RESOURCE_LOCAL_STORAGE } from '@/constants/localStorageKeyConstants';
import { getObfuscatedPath } from '@/utils/apiRoute';
import MobileHeaderContent from './HeaderContent/header';


// Create a context for the fetch function
export const AppContext = createContext();
const { useBreakpoint } = Grid;
// Initialize QueryClient once outside the component to prevent re-creation
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 2,
    },
  },
});

function App() {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { isLoggedIn } = useSelector(selectAuth);
  useIdleTimer(isLoggedIn);
  const obfuscatedPath = getObfuscatedPath('initiatives');
  const workstreamaction = getObfuscatedPath('workstreamaction');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);


  // State for all the data that needs to be loaded
  const [resourceResult, setResourceResult] = useState(null);
  const [workstreamActionsData, setWorkstreamActionsData] = useState([]);
  const [initiativesData, setInitiativesData] = useState([]);
  const [isInitiativesLoading, setIsInitiativesLoading] = useState(false); // New
  const [isInitiativesError, setIsInitiativesError] = useState(false);
  // Track completion status of each data load
  const [dataCompletion, setDataCompletion] = useState({
    resourceResult: false,
    workstreamActionsData: false,
    initiativesData: false,
    homeInitiatives: false
  });

  // Fetch action item data
  const fetchActionItemData = async (silent = false) => {
    try {
      const { result: itemResult } = await request.get({ entity: workstreamaction });
      const decryptedData = itemResult;
      setWorkstreamActionsData(decryptedData || []);
      if (!silent) {
        setDataCompletion(prev => ({ ...prev, workstreamActionsData: true }));
      }
    } catch (err) {
      console.error('Error fetching workstream action:', err);
      setWorkstreamActionsData([]);

      setDataCompletion(prev => ({ ...prev, workstreamActionsData: true }));
    }
  };
  // Fetch initiative data
  const fetchInitiativeData = async (silent = false) => {
    if (!silent) setIsInitiativesLoading(true);
    setIsInitiativesError(false);

    try {
      const { result } = await request.get({
        entity: `${obfuscatedPath}/getAll`,
      });

      const initiatives = result || [];
      setInitiativesData(initiatives);

      if (!silent) {
        setDataCompletion(prev => ({ ...prev, initiativesData: true }));
      }
    } catch (err) {
      console.error('Error fetching initiatives:', err);
      setInitiativesData([]);
      setIsInitiativesError(true);

      if (!silent) {
        setDataCompletion(prev => ({ ...prev, initiativesData: true }));
      }
    } finally {
      setIsInitiativesLoading(false);
    }
  };
  const fetchHomeInitiatives = async () => {
    try {
      // Removed page and limit params since the backend no longer uses them for initiatives
      const response = await request.get({
        entity: obfuscatedPath,
      });

      // The backend sends 'result', not 'initiativeResult'
      const initiativeResult = response.result || [];

      // Update React Query cache with the simplified key
      queryClient.setQueryData([obfuscatedPath], initiativeResult);

      // Mark as completed for initial loading
      if (!dataCompletion.homeInitiatives) {
        setDataCompletion((prev) => ({ ...prev, homeInitiatives: true }));
      }

      return initiativeResult;
    } catch (error) {
      console.error('Error fetching home initiatives:', error);

      // Ensure loading state is cleared even on error
      if (!dataCompletion.homeInitiatives) {
        setDataCompletion((prev) => ({ ...prev, homeInitiatives: true }));
      }
      throw error;
    }
  };
  useEffect(() => {
    if (!isLoggedIn) return;
    setIsLoading(true);

    // Decrypt resource result
    try {
      const decrypted = cryptoHelper.decrypt(window.localStorage.getItem(RESOURCE_LOCAL_STORAGE));
      setResourceResult(decrypted);
      setDataCompletion(prev => ({ ...prev, resourceResult: true }));
      console.log('resourceResult===',resourceResult);
    } catch (error) {
      console.error('Error decrypting resource:', error);
      setDataCompletion(prev => ({ ...prev, resourceResult: true }));
    }

    // Fetch all data in parallel, including Home initiatives
    Promise.allSettled([
      fetchActionItemData(),
      fetchInitiativeData(),
      fetchHomeInitiatives() // Call the function directly
    ]);
  }, [isLoggedIn]);

  // Monitor completion progress
  useEffect(() => {
    if (!isLoggedIn) return;

    const totalDataItems = Object.keys(dataCompletion).length;
    const completedItems = Object.values(dataCompletion).filter(Boolean).length;
    const progress = Math.round((completedItems / totalDataItems) * 100);

    setLoadingProgress(progress);

    // When all data is loaded (100%), wait a moment then hide loading
    if (progress === 100) {
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [dataCompletion, isLoggedIn]);

  const customAntdTheme = useMemo(() => ({
    token: {
      colorPrimary: '#025aa2',
      colorLink: '#025aa2',
      borderRadius: 4,
      colorTextHeading: '#10002b',
      colorText: '#2D3142',
      colorTextSecondary: '#4F5D75',
      colorTextDisabled: '#BFC0C0',
      colorBgContainer: 'rgba(249, 250, 252,1)',
      colorBgLayout: 'rgba(249, 250, 252,0.7)',
      fontWeightStrong: 600,
    },
  }), []);

  if (!isLoggedIn) {
    return <Router />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{
        background: '#253271'
      }}>
        <div className="text-center p-8 rounded-lg" style={{
          backgroundColor: '#253271',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(100, 130, 200, 0.2)',
          maxWidth: '400px',
          width: '90%'
        }}>
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#e6e8ff' }}>Welcome to StMS</h1>
          <div className="mb-6">
            <div className="h-1 w-24 bg-blue-400 mx-auto"></div>
          </div>
          <h2 className="text-xl mb-6" style={{ color: '#e6e8ff' }}>Getting data... {Math.round(loadingProgress)}%</h2>

          <div className="w-64 mx-auto mb-6">
            <Progress
              percent={Math.round(loadingProgress)}
              showInfo={false}
              strokeColor={{
                '0%': '#3b82f6',
                '100%': '#8b5cf6',
              }}
              trailColor="#1e293b"
              strokeWidth={4}
            />
          </div>

          <div className="flex justify-center">
            <div className="animate-pulse">
              <div className="h-12 w-12 rounded-full bg-blue-900 flex items-center justify-center" style={{ backgroundColor: 'rgba(30, 58, 138, 0.5)' }}>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="#60a5fa">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <AppContext.Provider value={{
      fetchHomeInitiatives,
      refreshInitiatives: fetchInitiativeData,
      refreshWorkstreamactions: fetchActionItemData
    }}>
      <QueryClientProvider client={queryClient}>
        <ConfigProvider theme={customAntdTheme}>
          <Layout className="min-h-screen">
            <Navigation resourceResult={resourceResult} />
            <Layout>
              {!isMobile ? (
                <HeaderContent
                  initiativesData={initiativesData}
                  workstreamActionsData={workstreamActionsData}
                />
              ) : (
                <MobileHeaderContent
                  initiativesData={initiativesData}
                  workstreamActionsData={workstreamActionsData}
                />
              )}

              <Layout.Content className="flex-1 sm:mb-0 mb-14">
                <Router isLoggedIn={true} />
              </Layout.Content>
              <AppFooter isLoggedIn={true} className="sm:sticky sm:bottom-0" />
            </Layout>
          </Layout>
        </ConfigProvider>
      </QueryClientProvider>
    </AppContext.Provider>
  );
}

export default App;